import type { ScheduleLink, ScheduleLinkType } from '@/lib/scheduleUtils';

/**
 * Canonical schedule link shape (predecessor → successor).
 * DB table: jphph_project_schedule_links
 *   predecessor_item_id → successor_item_id
 */
export interface CanonicalScheduleLink {
  id: string;
  predecessorId: string;
  successorId: string;
  type: ScheduleLinkType;
  lagDays: number;
}

export function normalizeScheduleLinks(raw: ScheduleLink[]): CanonicalScheduleLink[] {
  if (!Array.isArray(raw)) return [];
  const out: CanonicalScheduleLink[] = [];
  for (const row of raw) {
    if (!row?.id || !row.predecessor_item_id || !row.successor_item_id) continue;
    out.push({
      id: row.id,
      predecessorId: row.predecessor_item_id,
      successorId: row.successor_item_id,
      type: row.link_type,
      lagDays:
        typeof row.lag_days === 'number' && !Number.isNaN(row.lag_days)
          ? row.lag_days
          : Number(row.lag_days) || 0,
    });
  }
  return out;
}

export function buildSuccessorAdjacency(
  links: CanonicalScheduleLink[],
  excludeLinkId?: string | null
): Map<string, string[]> {
  const adj = new Map<string, string[]>();
  for (const link of links) {
    if (excludeLinkId && link.id === excludeLinkId) continue;
    if (!adj.has(link.predecessorId)) adj.set(link.predecessorId, []);
    adj.get(link.predecessorId)!.push(link.successorId);
  }
  return adj;
}

/** Can `fromId` reach `toId` following predecessor → successor edges? */
export function canReach(fromId: string, toId: string, links: CanonicalScheduleLink[]): boolean {
  if (!fromId || !toId) return false;
  if (fromId === toId) return true;
  const adj = buildSuccessorAdjacency(links);
  const visited = new Set<string>();
  const queue = [fromId];
  while (queue.length > 0) {
    const node = queue.shift()!;
    if (node === toId) return true;
    if (visited.has(node)) continue;
    visited.add(node);
    for (const next of adj.get(node) || []) {
      if (!visited.has(next)) queue.push(next);
    }
  }
  return false;
}

/**
 * Adding predecessorId → successorId creates a cycle iff successor can already reach predecessor.
 */
export function wouldCreateCircularDependency(
  predecessorId: string,
  successorId: string,
  links: ScheduleLink[],
  options?: { excludeLinkId?: string | null }
): boolean {
  if (!predecessorId || !successorId) return true;
  if (predecessorId === successorId) return true;

  const excludeLinkId = options?.excludeLinkId ?? null;
  const canonical = normalizeScheduleLinks(links).filter(
    (l) => !excludeLinkId || l.id !== excludeLinkId
  );

  if (
    canonical.some(
      (l) => l.predecessorId === successorId && l.successorId === predecessorId
    )
  ) {
    return true;
  }

  return canReach(successorId, predecessorId, canonical);
}

/** Path showing why a new pred→succ edge would loop (for UX). */
export function findCircularDependencyLoopLabels(
  predecessorId: string,
  successorId: string,
  links: ScheduleLink[],
  idToLabel?: Map<string, string>,
  excludeLinkId?: string | null
): string[] | null {
  const canonical = normalizeScheduleLinks(links).filter(
    (l) => !excludeLinkId || l.id !== excludeLinkId
  );
  if (!canReach(successorId, predecessorId, canonical)) return null;

  const adj = buildSuccessorAdjacency(canonical);
  const parent = new Map<string, string>();
  const visited = new Set<string>([successorId]);
  const queue = [successorId];
  let found = false;

  while (queue.length > 0) {
    const node = queue.shift()!;
    if (node === predecessorId) {
      found = true;
      break;
    }
    for (const next of adj.get(node) || []) {
      if (!visited.has(next)) {
        visited.add(next);
        parent.set(next, node);
        queue.push(next);
      }
    }
  }

  if (!found) return null;

  const segment: string[] = [];
  let cur = predecessorId;
  while (cur !== successorId) {
    segment.unshift(cur);
    const p = parent.get(cur);
    if (!p) break;
    cur = p;
  }
  segment.unshift(successorId);

  const label = (id: string) => idToLabel?.get(id) || id.slice(0, 8);
  return [label(predecessorId), ...segment.map(label), label(predecessorId)];
}

export type ScheduleLinkValidationResult =
  | { ok: true }
  | { ok: false; error: string };

export function validateScheduleLinkMutation(params: {
  predecessorId: string;
  successorId: string;
  links: ScheduleLink[];
  editingLinkId?: string | null;
  currentItemId?: string;
  idToLabel?: Map<string, string>;
}): ScheduleLinkValidationResult {
  const { predecessorId, successorId, links, editingLinkId, currentItemId, idToLabel } = params;

  if (!predecessorId || !successorId) {
    return { ok: false, error: 'Please select a schedule item.' };
  }
  if (predecessorId === successorId) {
    return { ok: false, error: 'A schedule item cannot be linked to itself.' };
  }
  if (
    currentItemId &&
    predecessorId !== currentItemId &&
    successorId !== currentItemId
  ) {
    return { ok: false, error: 'Link must involve the current schedule item.' };
  }

  const otherLinks = editingLinkId ? links.filter((l) => l.id !== editingLinkId) : links;
  const duplicate = otherLinks.some(
    (l) =>
      l.predecessor_item_id === predecessorId && l.successor_item_id === successorId
  );
  if (duplicate) {
    return { ok: false, error: 'This link already exists.' };
  }

  if (editingLinkId) {
    const existing = links.find((l) => l.id === editingLinkId);
    if (
      existing &&
      existing.predecessor_item_id === predecessorId &&
      existing.successor_item_id === successorId
    ) {
      return { ok: true };
    }
  }

  if (
    wouldCreateCircularDependency(predecessorId, successorId, links, {
      excludeLinkId: editingLinkId,
    })
  ) {
    const loop = findCircularDependencyLoopLabels(
      predecessorId,
      successorId,
      links,
      idToLabel,
      editingLinkId
    );
    if (loop && loop.length >= 2) {
      return { ok: false, error: `This would create a loop: ${loop.join(' → ')}` };
    }
    return { ok: false, error: 'This link would create a circular dependency.' };
  }

  return { ok: true };
}

/**
 * Lightweight self-check (no test framework required).
 * Returns false if any invariant fails.
 */
export function selfCheckScheduleDependencyEngine(): boolean {
  const link = (id: string, pred: string, succ: string): ScheduleLink => ({
    id,
    project_id: 'p1',
    predecessor_item_id: pred,
    successor_item_id: succ,
    link_type: 'FS',
    lag_days: 0,
    created_at: '',
  });

  const ab = [link('1', 'A', 'B')];
  if (wouldCreateCircularDependency('A', 'B', ab)) return false;
  if (!wouldCreateCircularDependency('B', 'A', ab)) return false;
  if (!wouldCreateCircularDependency('A', 'A', ab)) return false;

  const abcCycle = [link('1', 'A', 'B'), link('2', 'B', 'C'), link('3', 'C', 'A')];
  if (!wouldCreateCircularDependency('A', 'B', abcCycle)) return false;

  const abOnly = [link('1', 'A', 'B')];
  if (wouldCreateCircularDependency('A', 'B', abOnly, { excludeLinkId: '1' })) return false;

  const bc = [link('2', 'B', 'C')];
  // A→B is valid when B→C exists (no path from B back to A).
  if (wouldCreateCircularDependency('A', 'B', [...abOnly, ...bc])) return false;

  return true;
}
